import { ChangeEvent, useId, useState } from 'react';
import axios from 'axios';
import { isFileTypeAllowed } from '../../utils/fileHelper';
import { FileUploadProps } from './props';
import { addToast } from '../../utils/toastNotifications';
import { endpoints } from '../../utils/URL';

/**
 * Allows uploading a file by specifiying file formats and upload url.
 * @param callback Callback function that accept the File object and the File URL if any
 * @param uploadToRemote Indicates if we should do a remote upload with default URL
 * @param uploadUrl Specific URL to upload to, if not set, file is uploaded to default URL
 * @param allowedTypes An array of file types
 * @returns
 */
const FileUpload = ({
  callback,
  uploadToRemote,
  uploadUrl,
  allowedTypes,
  pharmacyCode,
}: FileUploadProps) => {
  const [progressState, setProgressState] = useState('progress-bar');
  const id = useId();

  const uploadFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || files?.length === 0) {
      addToast('Select a file', 'error');
      return;
    }

    let uploadedFile = files[0];

    if (!isFileTypeAllowed(uploadedFile, allowedTypes)) {
      addToast(`Wrong file. Upload ${fileTypes} file`, 'error');
      return;
    }

    if (uploadedFile.size > 2097152) {
      //if exceeding 2mb
      addToast('Exceed file limit. Max limit is 2MB', 'error');
      return;
    }

    if (!uploadToRemote && callback) {
      callback(uploadedFile, '');
      return;
    }

    event.preventDefault();

    const formData = new FormData();
    formData.append('formFile', uploadedFile);
    formData.append('pharmacyCode', pharmacyCode);

    const url = uploadUrl || `${endpoints.compliance.mainUrl}/files`;
    let progressBar = document.getElementById(`${id}-progress-bar`) as HTMLElement;
    progressBar.style.width = `0%`;

    var config = {
      onUploadProgress: function (progressEvent: any) {
        var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        progressBar.style.width = `${[percentCompleted]}%`;
      },
    };
    try {
      const response = await axios.post(url, formData, config);
      setProgressState('progress-bar bg-success');

      if (callback) {
        callback(uploadedFile, response.data);
      }
      addToast('Upload successful', 'success');
    } catch (error: any) {
      console.log(error);

      if (error.response && error.response.status === 422) {
        addToast('Please check the file type', 'error');
      } else {
        addToast('An Error occured. Check your network', 'error');
      }
      setProgressState('progress-bar bg-danger');
      progressBar.style.width = `0%`;
      setProgressState('progress-bar');
    } finally {
    }
  };

  const fileTypes =
    allowedTypes && allowedTypes.length ? allowedTypes.map((x) => x.toLowerCase()).join(', ') : '';

  return (
    <div className=''>
      <input
        className='form-control'
        type='file'
        onChange={uploadFile}
        required
        accept={fileTypes}
      />

      <div className={'progress'} style={{ height: '3px', marginTop: '10px' }}>
        <div
          id={`${id}-progress-bar`}
          className={progressState}
          role='progressbar'
          style={{ width: '0%' }}
          aria-valuenow={0}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      <small>Allowed files {fileTypes || 'All files'}</small>
    </div>
  );
};

export default FileUpload;
